// Helper utilities for checking agent capabilities based on reported agent version

export const MIN_RECORDING_AGENT_VERSION = "0.7.0";
/**
 * From this version the agent seals recordings every second and no longer
 * splits them by `split.max_duration_s`.
 */
export const MIN_SEALED_RECORDS_AGENT_VERSION = "1.0.0";
/**
 * Build-time fallback for the newest agent version.
 *
 * The real value comes from the apt repository via `GET /agent/releases` — use
 * `useLatestAgentVersion()` in components. This constant is only what gets
 * rendered while that query is in flight or if it fails, so it needs to be
 * roughly current, not exact.
 */
export const FALLBACK_LATEST_AGENT_VERSION = "0.10.0";

export type AgentVersionUpdateState =
  | "unknown-version"
  | "up-to-date"
  | "update-available";

function parseSemver(version?: string | null): [number, number, number] | null {
  if (!version) return null;

  // Accept versions like 0.6.0 or v0.6.0 and ignore suffixes like -beta.1
  const m = version.trim().match(/^v?(\d+)\.(\d+)\.(\d+)/i);
  if (!m) return null;

  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

export function isAgentVersionAtLeast(
  agentVersion: string | null | undefined,
  minVersion: string,
): boolean {
  const current = parseSemver(agentVersion);
  const minimum = parseSemver(minVersion);
  if (!current || !minimum) return false;

  const [currentMajor, currentMinor, currentPatch] = current;
  const [minMajor, minMinor, minPatch] = minimum;

  if (currentMajor !== minMajor) return currentMajor > minMajor;
  if (currentMinor !== minMinor) return currentMinor > minMinor;
  return currentPatch >= minPatch;
}

export function getAgentVersionUpdateState(
  agentVersion: string | null | undefined,
  latestVersion: string = FALLBACK_LATEST_AGENT_VERSION,
): AgentVersionUpdateState {
  const current = parseSemver(agentVersion);
  const latest = parseSemver(latestVersion);

  if (!current || !latest) {
    return "unknown-version";
  }

  return isAgentVersionAtLeast(agentVersion, latestVersion)
    ? "up-to-date"
    : "update-available";
}

export function isAgentUpdateAvailable(
  agentVersion?: string | null,
  latestVersion: string = FALLBACK_LATEST_AGENT_VERSION,
): boolean {
  return (
    getAgentVersionUpdateState(agentVersion, latestVersion) ===
    "update-available"
  );
}

/**
 * Returns true if the agent version is present and is >= 0.7.0
 * Treats missing/null/invalid versions as not supported.
 */
export function isRecordingSupported(agentVersion?: string | null): boolean {
  return isAgentVersionAtLeast(agentVersion, MIN_RECORDING_AGENT_VERSION);
}

/**
 * Returns true if the agent splits recordings by `split.max_duration_s`:
 * versions before 1.0.0. Missing or invalid versions count as older agents, so
 * the setting stays reachable for them.
 */
export function usesRecordingSplitDuration(
  agentVersion?: string | null,
): boolean {
  return !isAgentVersionAtLeast(agentVersion, MIN_SEALED_RECORDS_AGENT_VERSION);
}
